/**
 * Razorpay order creation via Firebase Callable Functions (same pattern as sadhuramedutech).
 * Requires Firebase Auth user session (email/password login).
 */
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { app, auth } from "../api/firebase";

const region =
  process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || "asia-south1";

let emulatorConnected = false;

function getFunctionsInstance() {
  if (!app) {
    throw new Error(
      "Firebase is not configured. Add REACT_APP_FIREBASE_* to `.env` and restart the dev server."
    );
  }
  const functions = getFunctions(app, region);
  if (process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === "true" && !emulatorConnected) {
    connectFunctionsEmulator(functions, "localhost", 5001);
    emulatorConnected = true;
  }
  return functions;
}

export async function ensureAuthedUser() {
  if (!auth) {
    throw new Error(
      "Firebase Auth is not available. Check your `.env` Firebase keys."
    );
  }
  if (!auth.currentUser) {
    throw new Error("Please login with email/password before continuing.");
  }
  return auth.currentUser.uid;
}

/**
 * @returns {Promise<{ orderId: string, amount: number, currency: string, keyId: string, internalOrderId: string }>}
 */
export async function createRazorpayOrderCallable(product, amountRupees) {
  await ensureAuthedUser();
  const createOrder = httpsCallable(getFunctionsInstance(), "createRazorpayOrder");
  let data;
  try {
    const result = await createOrder({
      amountRupees: Number(amountRupees),
      product: {
        id: product.id,
        title: String(product.Tittle || ""),
        description: String(product.Description || ""),
      },
    });
    data = result.data;
  } catch (e) {
    const msg =
      (e && e.message) ||
      (e && e.details) ||
      "createRazorpayOrder failed. Deploy functions and enable Anonymous sign-in.";
    throw new Error(String(msg));
  }

  if (!data?.razorpayOrderId) {
    throw new Error("Invalid response from createRazorpayOrder.");
  }

  return {
    orderId: data.razorpayOrderId,
    amount: data.amount,
    currency: data.currency || "INR",
    keyId: data.keyId,
    internalOrderId: data.internalOrderId,
  };
}

/**
 * Server-side signature verification (writes `paid` on the e-card order in RTDB).
 */
export async function verifyRazorpayPaymentCallable({
  internalOrderId,
  razorpay_order_id,
  razorpay_payment_id,
  razorpay_signature,
}) {
  await ensureAuthedUser();
  const verify = httpsCallable(getFunctionsInstance(), "verifyRazorpayPayment");
  const { data } = await verify({
    internalOrderId,
    razorpay_order_id,
    razorpay_payment_id,
    razorpay_signature,
  });
  return data;
}

/**
 * Saves post-payment e-card payload under the buyer's order.
 * This remains private until publishPaidEcardGiftCallable is called.
 */
export async function savePaidEcardDraftCallable({
  internalOrderId,
  giftkartCategoryId,
  gift,
}) {
  await ensureAuthedUser();
  const fn = httpsCallable(getFunctionsInstance(), "savePaidEcardDraft");
  const { data } = await fn({
    internalOrderId: String(internalOrderId || ""),
    giftkartCategoryId: String(giftkartCategoryId || ""),
    gift: gift || {},
  });
  return data;
}

/**
 * Writes paid e-card draft to RTDB `giftkart/…` and returns public link data.
 * @returns {Promise<{ giftKey: string, giftkartCategoryId: string, shortCode?: string }>}
 */
export async function publishPaidEcardGiftCallable({
  internalOrderId,
  giftkartCategoryId,
}) {
  await ensureAuthedUser();
  const fn = httpsCallable(getFunctionsInstance(), "publishPaidEcardGift");
  let data;
  try {
    const result = await fn({
      internalOrderId: String(internalOrderId || ""),
      giftkartCategoryId: String(giftkartCategoryId || ""),
    });
    data = result.data;
  } catch (e) {
    const msg =
      (e && e.message) ||
      (e && e.details) ||
      "publishPaidEcardGift failed. Deploy Cloud Functions.";
    throw new Error(String(msg));
  }
  if (!data?.giftKey) {
    throw new Error("Invalid response from publishPaidEcardGift.");
  }
  return {
    giftKey: String(data.giftKey),
    giftkartCategoryId: String(data.giftkartCategoryId || giftkartCategoryId),
    shortCode: data.shortCode ? String(data.shortCode) : undefined,
  };
}

export async function upsertEcardPinAccountCallable({ email, pin }) {
  await ensureAuthedUser();
  const fn = httpsCallable(getFunctionsInstance(), "upsertEcardPinAccount");
  const { data } = await fn({
    email: String(email || ""),
    pin: String(pin || ""),
  });
  return data;
}

export async function loginWithEcardPinCallable({ email, pin }) {
  await ensureAuthedUser();
  const fn = httpsCallable(getFunctionsInstance(), "loginWithEcardPin");
  const { data } = await fn({
    email: String(email || ""),
    pin: String(pin || ""),
  });
  return data;
}

/**
 * Legacy HTTP helper (local Express `server/` or another host). Used only if
 * REACT_APP_USE_HTTP_CHECKOUT=true — otherwise callables are used.
 */
export function getCheckoutApiBase() {
  const base = process.env.REACT_APP_CHECKOUT_API_URL || "";
  return base.replace(/\/$/, "");
}

export async function createRazorpayOrderHttp({ amountPaise, receipt, notes }) {
  const prefix = getCheckoutApiBase();
  const url = `${prefix}/api/create-order`;

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ amountPaise, receipt, notes }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Order failed (${res.status})`);
  }
  return data;
}
